import React from 'react';
import { Card, CardSkeleton } from '@/components/ui';
import type { FeedbackMetrics } from '@/types/feedback';

interface FeedbackMetricsCardProps {
  metrics: FeedbackMetrics | null;
  loading: boolean;
}

interface MetricItemProps {
  label: string;
  value: number | string;
  subLabel?: string;
  color?: 'primary' | 'success' | 'error';
}

const MetricItem: React.FC<MetricItemProps> = ({
  label,
  value,
  subLabel,
  color = 'primary',
}) => {
  const colorClasses = {
    primary: 'text-primary-600',
    success: 'text-green-600',
    error: 'text-red-600',
  };

  return (
    <div className="text-center p-4">
      <p className={`text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
      <p className="text-sm font-medium text-secondary-700">{label}</p>
      {subLabel && <p className="text-xs text-secondary-500">{subLabel}</p>}
    </div>
  );
};

export const FeedbackMetricsCard: React.FC<FeedbackMetricsCardProps> = ({
  metrics,
  loading,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  if (!metrics) {
    return (
      <Card padding="lg">
        <p className="text-secondary-500">No feedback metrics available</p>
      </Card>
    );
  }

  const positivePercent = Math.round(metrics.positive_ratio * 100);

  return (
    <Card padding="lg">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">
        Feedback Overview
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricItem label="Total Feedback" value={metrics.total_feedback} />
        <MetricItem
          label="Positive"
          value={metrics.positive_count}
          color="success"
        />
        <MetricItem
          label="Negative"
          value={metrics.negative_count}
          color="error"
        />
        <MetricItem
          label="Positive Ratio"
          value={`${positivePercent}%`}
          color={
            positivePercent >= 70
              ? 'success'
              : positivePercent >= 50
                ? 'primary'
                : 'error'
          }
        />
      </div>

      {/* Positive ratio bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-secondary-600">Satisfaction Rate</span>
          <span className="font-medium">{positivePercent}%</span>
        </div>
        <div className="h-3 bg-secondary-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              positivePercent >= 70
                ? 'bg-green-500'
                : positivePercent >= 50
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
            style={{ width: `${positivePercent}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-secondary-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-secondary-700 mb-3">Today</h4>
          <p className="text-2xl font-bold text-primary-600">
            {metrics.feedback_today}
          </p>
          <p className="text-xs text-secondary-500">feedback submitted</p>
        </div>

        <div className="border border-secondary-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-secondary-700 mb-3">
            This Week
          </h4>
          <p className="text-2xl font-bold text-primary-600">
            {metrics.feedback_this_week}
          </p>
          <p className="text-xs text-secondary-500">feedback submitted</p>
        </div>

        <div className="border border-secondary-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-secondary-700 mb-3">
            This Month
          </h4>
          <p className="text-2xl font-bold text-primary-600">
            {metrics.feedback_this_month}
          </p>
          <p className="text-xs text-secondary-500">feedback submitted</p>
        </div>
      </div>
    </Card>
  );
};

export default FeedbackMetricsCard;
